/*
  NUDIENT: Spårningspaketet, M-028 och M-032, förberett 19 oktober 2026
  enligt M-108 tillägget punkt 3. Utredning: nudient/UTREDNING-sparning.md,
  avsnittet "Förberett 19 oktober 2026". Laddas av
  snippets/nudient-tracking.liquid, inkopplad 19 oktober 2026 enligt
  M-108 tillägget punkt 2, sist i layout/theme.liquid.

  Gör det det gamla temats Woolman.GTM gjorde, assets/global.js:591 till
  690 i det gamla temat, med samma namn och samma innehåll, migrera först:

  - cartItems: varukorgen sparas i webbläsarens lagring under namnet
    cartItems vid varje sidladdning och efter varje ändring av varukorgen.
    Det gamla temat lyssnade på Taigas ajaxProduct:added och cart:update.
    Här lyssnar skriptet på Shopifys shopify:cart:lines-update, som Horizon
    skickar vid lägg i, ändra och ta bort, och väntar tills ändringen är
    klar. Varukorgen hämtas från sections/nudient-tracking-data.liquid i
    stället för /cart?view=gtm.
  - collectionView-v2: kollektionens produkter till dataLayer, bara efter
    ett filterbyte eller en ny sortering, som idag. Det gamla temat
    lyssnade på Taigas collection:update. Här lyssnar skriptet på Shopifys
    shopify:collection:update, som Horizons filter skickar, och väntar tills
    sidan är omritad, som Taiga gjorde.
  - productClick-v2: klick på ett produktkort i kollektionens rutnät till
    dataLayer, bara efter det första filterbytet, som idag. Det gamla temat
    satte en lyssnare på varje kort med klassen card--product. Här lyssnar
    skriptet på hela sidan och letar upp kortet, Horizons
    .product-grid__item med data-product-id, och läser produkten ur samma
    svar som collectionView-v2.

  Anpassningar för Horizon, som ska skrivas in i M-028 när paketet kopplas
  in: händelserna ovan, sektionen i stället för mallen cart.gtm, och att
  dataLayer skapas om Tag Manager inte har gjort det ännu. Det gamla temat
  förutsatte att dataLayer fanns.

  Väntar på samtycke, M-164, ett undantag från "migrera först": skriptet
  startar först när Shopifys samtycke för analys är givet,
  Shopify.customerPrivacy.analyticsProcessingAllowed(), som Pandectes
  uppdaterar. Utan samtycke än väntar det på Shopifys händelse
  visitorConsentCollected och startar direkt när kunden godkänner, utan
  omladdning. Före samtycke och efter "Neka alla" hämtas och sparas inget,
  och inget går till dataLayer. Saknas Shopifys samtyckesgränssnitt startar
  skriptet inte. Om Tag Manager skickar vidare styrs som förut av Consent
  Mode i Tag Manager.

  Ingen händelse för val av telefonmodell skickas till dataLayer. Det finns
  inget beslut om det. Modellvalet går redan till Customer events som
  nudient:model_change, assets/nudient-model-store.js.

  Värden som inte är tokens:
  - nudient-tracking: id på skripttaggen i snippets/nudient-tracking.liquid.
  - nudient-tracking-data: sektionen som svarar med varukorgen och
    kollektionen.
  - shopify:cart:lines-update och shopify:collection:update: Shopifys
    standardhändelser, biblioteket @shopify/events som Horizon använder.
  - .product-grid__item[data-product-id] och [data-testid="product-grid"]:
    kortet och rutnätet i sections/nudient-collection-grid.liquid.
  - productClick-v2, collectionView-v2 och cartItems: namnen i det gamla
    temat, som regler i Tag Manager kan leta efter.
  - consent-tracking-api och 0.1: Shopifys namn och version för
    samtyckesgränssnittet i loadFeatures.
  - visitorConsentCollected: Shopifys händelse när kunden har svarat.
*/

(() => {
  const config = document.getElementById('nudient-tracking');
  const rawRoot = config?.dataset.root || '/';
  const root = rawRoot === '/' ? '/' : `${rawRoot}/`;
  const template = config?.dataset.template || '';
  const SECTION = 'nudient-tracking-data';

  /** Senaste svaret för kollektionen, per produkt-id. Tomt tills första filterbytet. */
  let productsById = null;

  const currency = () => window.Shopify?.currency?.active;

  const pushToDataLayer = (data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  };

  /** Läser ett JSON-block ur ett svar från Section Rendering API. */
  const fetchSectionJson = async (url, attribute) => {
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const script = doc.querySelector(`script[${attribute}]`);
    return script ? JSON.parse(script.textContent) : null;
  };

  /** Samma objekt som Woolman.GTM.getProductObjectStandard i det gamla temat. */
  const getProductObjectStandard = (product) => ({
    id: product.sku,
    name: product.title,
    productId: product.productId,
    variantId: product.variantId,
    handle: product.handle,
    sku: product.sku,
    price: product.price,
    category: product.category,
    brand: product.vendor,
    quantity: 1,
    variant: product.variant,
    barcode: product.barcode,
    image: product.image,
    position: product.position,
    list: product.list,
  });

  /** cartItems, som Woolman.GTM.updateLocaleStorage. */
  const updateLocalStorage = async () => {
    try {
      const url = new URL(`${root}cart`, window.location.origin);
      url.searchParams.set('section_id', SECTION);
      const data = await fetchSectionJson(url, 'data-nudient-tracking-cart');
      if (!Array.isArray(data)) return;
      const cartItems = data.map((item) => ({
        id: item.id,
        product_id: item.product_id,
        variant_id: item.variant_id,
        title: item.title,
        quantity: item.quantity,
        price: item.price,
        line_price: item.line_price,
        sku: item.sku,
        vendor: item.vendor,
        product_type: item.product_type,
        requires_shipping: item.requires_shipping,
        variant_title: item.variant_title,
        url: item.url,
        barcode: item.barcode,
      }));
      localStorage.setItem('cartItems', JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
  };

  /** collectionView-v2, som Woolman.GTM.handleCollectionImpressions. */
  const handleCollectionUpdate = async () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', SECTION);
      const products = await fetchSectionJson(url, 'data-nudient-tracking-collection');
      if (!Array.isArray(products)) return;

      productsById = new Map(products.map((product) => [String(product.productId), product]));

      pushToDataLayer({
        event: 'collectionView-v2',
        ecommerce: {
          currencyCode: currency(),
          impressions: products.map(getProductObjectStandard),
        },
      });
    } catch (error) {
      console.error('Error fetching collection data:', error);
    }
  };

  /** productClick-v2, som Woolman.GTM.handleProductClick. */
  const handleProductClick = (event) => {
    if (!productsById) return;
    const target = event.target instanceof Element ? event.target : null;
    const card = target?.closest('[data-testid="product-grid"] .product-grid__item[data-product-id]');
    if (!card) return;
    const product = productsById.get(String(card.getAttribute('data-product-id')));
    if (!product) return;

    pushToDataLayer({
      event: 'productClick-v2',
      ecommerce: {
        currencyCode: currency(),
        click: {
          products: [getProductObjectStandard(product)],
        },
      },
    });
  };

  /** Väntar tills Horizon är klar med ändringen. Händelsen bär ett löfte. */
  const afterPromise = (event, callback) => {
    Promise.resolve(event?.promise)
      .catch(() => {})
      .then(callback);
  };

  let started = false;

  /** Startar paketet en gång, först efter samtycke för analys, M-164. */
  const start = () => {
    if (started) return;
    started = true;
    updateLocalStorage();
    document.addEventListener('shopify:cart:lines-update', (event) => afterPromise(event, updateLocalStorage));

    if (template === 'collection') {
      document.addEventListener('shopify:collection:update', (event) => afterPromise(event, handleCollectionUpdate));
      document.addEventListener('click', handleProductClick, true);
    }
  };

  const analyticsAllowed = () => {
    try {
      return Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.());
    } catch {
      return false;
    }
  };

  /* Kunden svarar på cookierutan: startar direkt vid samtycke, utan omladdning */
  document.addEventListener('visitorConsentCollected', (event) => {
    if (event?.detail?.analyticsAllowed || analyticsAllowed()) start();
  });

  /* Samtycke från ett tidigare besök */
  const check = () => {
    if (analyticsAllowed()) start();
  };
  if (window.Shopify?.customerPrivacy) {
    check();
  } else {
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) check();
    });
  }
})();
